import type { ProductDetail, SetProductLogoRequest } from '@avrahami/contracts';
import { Button, Card } from '@avrahami/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys, productQuery, tenantApi } from '../api/tenant';
import { he } from '../i18n/he';
import { FormStatus, ImageAdder, Thumbnail } from './fields';

/**
 * The manufacturer logo the store shows on the product (adr/0041): the catalog's, as on the
 * original site, one the store uploads instead, or none. Every choice is saved at once.
 */
export function ProductLogoCard({
  product,
  canWrite,
}: {
  product: ProductDetail;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (logo: SetProductLogoRequest['logo']) =>
      tenantApi.setProductLogo(product.id, { version: product.version, logo }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(keys.product(product.id), updated);
      await queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
  const reload = () => {
    save.reset();
    // A failed read shows on the page itself (the product query's error).
    void queryClient
      .query({ ...productQuery(product.id), staleTime: 0 })
      .then(undefined, () => undefined);
  };
  const { logo } = product;
  const fromCatalog = product.origin === 'MASTER_CATALOG';
  const note =
    logo.source === 'CATALOG'
      ? logo.file === null
        ? he.products.logo.catalogNone
        : he.products.logo.CATALOG
      : he.products.logo[logo.source];

  return (
    <Card title={he.products.logo.title}>
      <div className="flex flex-col gap-3" data-testid="product-logo" data-source={logo.source}>
        <div className="flex flex-wrap items-center gap-3">
          {logo.file === null ? (
            <span className="text-xs text-slate-500">{he.products.logo.none}</span>
          ) : (
            <Thumbnail file={logo.file} alt={he.products.logo.alt} size={80} />
          )}
          {/* Not shown for the store's own product without a logo: there is nothing to explain. */}
          {fromCatalog || logo.source !== 'CATALOG' ? (
            <p className="min-w-0 flex-1 text-sm text-slate-700" data-testid="product-logo-note">
              {note}
            </p>
          ) : null}
        </div>
        {canWrite ? (
          <>
            <ImageAdder
              label={logo.file === null ? he.products.logo.upload : he.products.logo.replace}
              onUploaded={(media) => {
                save.mutate({ source: 'UPLOAD', mediaId: media.id });
              }}
              testId="product-logo-upload"
            />
            <div className="flex flex-wrap gap-2">
              {logo.file === null ? null : (
                <Button
                  tone="secondary"
                  busy={save.isPending && save.variables.source === 'NONE'}
                  data-testid="product-logo-remove"
                  onClick={() => {
                    save.mutate({ source: 'NONE' });
                  }}
                >
                  {he.products.logo.remove}
                </Button>
              )}
              {fromCatalog && logo.source !== 'CATALOG' ? (
                <Button
                  tone="secondary"
                  busy={save.isPending && save.variables.source === 'CATALOG'}
                  data-testid="product-logo-catalog"
                  onClick={() => {
                    save.mutate({ source: 'CATALOG' });
                  }}
                >
                  {he.products.logo.backToCatalog}
                </Button>
              ) : null}
            </div>
            <FormStatus
              error={save.error}
              localInvalid={false}
              saved={save.isSuccess}
              onReload={reload}
            />
          </>
        ) : null}
      </div>
    </Card>
  );
}
